/* Splus display adapter. FoxKit's prices, forms, events and requests stay untouched. */
(() => {
  'use strict';
  const configNode = document.getElementById('splus-vat-config');
  let config;
  try { config = JSON.parse(configNode?.textContent || '{}'); } catch { return; }
  if (config.percent !== 115 || !window.SplusVatDisplay) return;
  const ar = (document.documentElement.lang || '').startsWith('ar');
  const pendingText = ar ? 'جارٍ حساب السعر…' : 'Calculating price…';
  const unavailableText = ar ? 'تحقق من السعر في السلة' : 'Check price in cart';
  const selectors = [
    '.foxkit-product-card__price', '.foxkit-product-card__compare-price',
    '.foxkit-product-bundles__total-amount', '.foxkit-product-bundles__sub-total',
    '.foxkit-product-bundles__save-amount'
  ].join(',');
  const products = new Map();
  const copies = new Map();
  const cartOriginals = new WeakMap();
  const goalNotes = new Map();
  let scheduled = false;
  const format = cents => (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + (ar ? ' ر.س' : ' SAR');
  function readMinor(text) {
    const normalized = text.replace(/[\u200e\u200f\u061c]/g, '').replace(/[٠-٩]/g, c => String(c.charCodeAt(0) - 1632)).replace(/٬/g, ',').replace(/٫/g, '.');
    const numbers = normalized.match(/\d[\d,]*(?:\.\d{1,2})?/g);
    if (!numbers || numbers.length !== 1 || /%|٪/.test(normalized)) return null;
    const number = Number(numbers[0].replace(/,/g, ''));
    return Number.isFinite(number) ? Math.round(number * 100) : null;
  }
  function productPath(card) {
    const anchor = card.querySelector('a[href*="/products/"]');
    if (!anchor) return null;
    try {
      const url = new URL(anchor.getAttribute('href'), location.origin);
      if (url.origin !== location.origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) return null;
      return url.pathname.replace(/\/$/, '') + '.js';
    } catch { return null; }
  }
  function productState(card) {
    const path = productPath(card);
    if (!path) return { status: 'failed' };
    if (!products.has(path)) {
      const state = { status: 'loading' };
      products.set(path, state);
      fetch(path, { credentials: 'same-origin', signal: globalThis.AbortSignal?.timeout?.(10000) })
        .then(response => { if (!response.ok) throw new Error('Product unavailable'); return response.json(); })
        .then(product => {
          if (String(product.id) !== card.getAttribute('data-product-id') || !Array.isArray(product.variants)) throw new Error('Product mismatch');
          state.product = product;
          state.status = 'ready';
        })
        .catch(() => { state.status = 'failed'; })
        .finally(schedule);
    }
    return products.get(path);
  }
  function taxableState(card) {
    const state = productState(card);
    if (state.status !== 'ready') return state.status;
    const id = card.querySelector('[name="variant-id"]')?.value;
    const variants = state.product.variants;
    const variant = variants.find(v => String(v.id) === String(id));
    if (variant && typeof variant.taxable === 'boolean') return variant.taxable;
    // Related-product cards may not expose a variant. Only use a uniform known tax status.
    if (variants.length && variants.every(v => v.taxable === true)) return true;
    if (variants.length && variants.every(v => v.taxable === false)) return false;
    return 'failed';
  }
  function factorFor(source) {
    const card = source.closest('foxkit-product-card');
    if (card) return taxableState(card);
    const bundle = source.closest('.foxkit-product-bundles');
    if (!bundle) return 'failed';
    const cards = [...bundle.querySelectorAll('foxkit-product-card')];
    const states = cards.map(taxableState);
    if (states.some(s => s === 'loading')) return 'loading';
    if (states.length && states.every(s => s === true)) return true;
    if (states.length && states.every(s => s === false)) return false;
    // Never apply a blanket rate to a bundle with mixed or unknown tax treatment.
    return 'failed';
  }
  function isHidden(source) {
    return source.hidden || source.classList.contains('foxkit-hidden') || source.classList.contains('!foxkit-hidden') || source.style.display === 'none';
  }
  function undiscountedBundleAmount(source) {
    const bundle = source.closest('.foxkit-product-bundles--no-discount');
    if (!bundle || !source.matches('[data-total],[data-sub-total],[data-saved-price]')) return null;
    const selected = [...bundle.querySelectorAll('foxkit-product-card')].filter(card => card.classList.contains('foxkit-product-card--main') || card.querySelector('.foxkit-product-card__button.selected') || card.querySelector('.foxkit-product-card__checkbox')?.checked);
    if (!selected.length) return null;
    let net = 0, gross = 0, compare = 0;
    for (const card of selected) {
      const taxable = taxableState(card);
      const price = readMinor(card.querySelector('.foxkit-product-card__price')?.textContent || '');
      const comparison = readMinor(card.querySelector('.foxkit-product-card__compare-price')?.textContent || '') ?? price;
      const quantity = Number(card.querySelector('[name="quantity"]')?.value || 1);
      if (typeof taxable !== 'boolean' || price === null || !Number.isInteger(quantity) || quantity < 1) return null;
      net += price * quantity;
      gross += window.SplusVatDisplay.amount(price * quantity, taxable);
      compare += window.SplusVatDisplay.amount(Math.max(price, comparison) * quantity, taxable);
    }
    // Only use per-line rounding when FoxKit's current selection agrees with these lines.
    if (net !== readMinor(bundle.querySelector('[data-sub-total]')?.textContent || '')) return null;
    return source.matches('[data-total]') ? compare : source.matches('[data-saved-price]') ? compare - gross : gross;
  }
  function updateCartSummaries() {
    document.querySelectorAll('.splus-vat-cart-summary').forEach(row => {
      const target = row.querySelector('[data-splus-vat-cart-total]');
      const proxy = row.previousElementSibling?.querySelector('[data-cart-subtotal-price]');
      const note = row.nextElementSibling?.matches('[data-splus-vat-cart-note]') ? row.nextElementSibling : null;
      if (!target || !proxy || !note) return;
      if (!cartOriginals.has(target)) cartOriginals.set(target, { html: target.innerHTML, gross: readMinor(target.textContent) });
      const original = cartOriginals.get(target);
      const serverNet = Number(row.getAttribute('data-splus-server-net'));
      let currentNet = readMinor(proxy.textContent);
      const offer = row.parentElement.querySelector('.foxkit-cart-discounts');
      const offerAmounts = offer ? [...offer.querySelectorAll('li')].map(item => readMinor(item.textContent)) : [];
      const allocatedAttribute = row.getAttribute('data-splus-foxkit-allocated');
      const allocated = allocatedAttribute === null ? null : Number(allocatedAttribute);
      // On return from checkout FoxKit can include a remembered shipping fee in
      // its subtotal. Use its explicit offer amount against Shopify's subtotal,
      // deducting only the amount not already allocated to an FX- discount code.
      if (offerAmounts.length && offerAmounts.every(amount => amount !== null) && allocated !== null && Number.isFinite(allocated)) {
        const pending = Math.max(0, offerAmounts.reduce((sum, amount) => sum + amount, 0) - allocated);
        currentNet = Math.max(0, serverNet - pending);
      }
      if (currentNet === serverNet) {
        if (target.innerHTML !== original.html) target.innerHTML = original.html;
        if (!note.hidden) note.hidden = true;
        return;
      }
      // FoxKit's legacy code method can adjust the displayed subtotal before Shopify
      // returns discount allocations. Never treat its raw number as an already-taxed total.
      const mode = row.getAttribute('data-splus-cart-tax-mode');
      const known = currentNet !== null && currentNet >= 0 && currentNet <= serverNet && ['taxable', 'exempt'].includes(mode);
      const gross = known ? window.SplusVatDisplay.amount(currentNet, mode === 'taxable') : null;
      const value = known ? format(gross) : (ar ? 'يُحدد عند الدفع' : 'Confirmed at checkout');
      if (target.textContent !== value) target.textContent = value;
      const saving = known && original.gross !== null ? Math.max(0, original.gross - gross) : null;
      const message = saving !== null
        ? (ar ? 'يشمل خصم عرض تقديرياً بقيمة ' + format(saving) + '؛ المبلغ النهائي عند الدفع.' : 'Includes an estimated offer discount of ' + format(saving) + '; final amount at checkout.')
        : (ar ? 'يتضمن عرضاً لم يُعتمد بعد؛ تُحسب الضريبة والخصومات النهائية عند الدفع.' : 'An offer is pending; final tax and discounts are calculated at checkout.');
      if (note.textContent !== message) note.textContent = message;
      if (note.hidden) note.hidden = false;
    });
  }
  function update() {
    scheduled = false;
    // The merchant deferred the free-shipping threshold. FoxKit can include a
    // remembered shipping fee in its progress calculation after checkout, so
    // do not display a numeric promise or a success state from that estimate.
    for (const [goal, note] of goalNotes) {
      if (!goal.isConnected || !note.isConnected) { note.remove(); goalNotes.delete(goal); }
    }
    document.querySelectorAll('foxkit-cart-goal').forEach(goal => {
      if (!goal.hidden) goal.hidden = true;
      if (goal.style.display !== 'none') goal.style.setProperty('display', 'none', 'important');
      if (!goalNotes.has(goal)) {
        const note = document.createElement('p');
        note.setAttribute('data-splus-vat-goal-note', '');
        note.textContent = ar ? 'تُحدد أهلية الشحن المجاني عند الدفع.' : 'Free-shipping eligibility is confirmed at checkout.';
        goal.insertAdjacentElement('afterend', note);
        goalNotes.set(goal, note);
      }
    });
    for (const [source, copy] of copies) {
      if (!source.isConnected || !copy.isConnected) { copy.remove(); copies.delete(source); }
    }
    document.querySelectorAll(selectors).forEach(source => {
      const minor = readMinor(source.textContent);
      let copy = copies.get(source);
      if (!copy) {
        copy = document.createElement(source.tagName === 'DEL' ? 'del' : 'span');
        copy.className = 'splus-foxkit-vat-copy';
        copy.setAttribute('data-splus-vat-copy', '');
        copy.setAttribute('aria-live', 'polite');
        source.insertAdjacentElement('afterend', copy);
        source.setAttribute('data-splus-vat-source', '');
        source.setAttribute('aria-hidden', 'true');
        copies.set(source, copy);
      }
      const state = factorFor(source);
      const display = typeof state === 'boolean' && minor !== null ? undiscountedBundleAmount(source) ?? window.SplusVatDisplay.amount(minor, state) : null;
      const text = state === 'loading' ? pendingText : state === 'failed' ? unavailableText : minor === null ? (source.textContent.trim() ? unavailableText : '') : format(display);
      if (copy.textContent !== text) copy.textContent = text;
      const hidden = isHidden(source) || text === '';
      if (copy.hidden !== hidden) copy.hidden = hidden;
      if (source.closest('.foxkit-product-bundles__prices')) {
        copy.title = ar ? 'تقديري شامل الضريبة؛ المبلغ والأهلية النهائية عند الدفع' : 'Estimated VAT-inclusive amount; final amount and eligibility at checkout';
      }
    });
    updateCartSummaries();
  }
  function schedule() {
    if (!scheduled) { scheduled = true; requestAnimationFrame(update); }
  }
  const ownSelector = '[data-splus-vat-copy],[data-splus-vat-cart-total],[data-splus-vat-cart-note],[data-splus-vat-goal-note]';
  const owned = node => node.nodeType === 1 ? node.matches(ownSelector) || node.closest(ownSelector) : node.parentElement?.closest(ownSelector);
  const observedSelector = 'foxkit-product-card,.foxkit-product-bundles,foxkit-cart-goal,[data-cart-subtotal],.splus-vat-cart-summary,.foxkit-cart-discounts';
  new MutationObserver(records => {
    if (records.some(record => {
      if (owned(record.target)) return false;
      if (record.type === 'childList' && [...record.addedNodes, ...record.removedNodes].length && [...record.addedNodes, ...record.removedNodes].every(owned)) return false;
      return record.target.nodeType === 1 ? !!record.target.closest(observedSelector) || [...record.addedNodes].some(n => n.nodeType === 1 && (n.matches(observedSelector) || n.querySelector(observedSelector))) : !!record.target.parentElement?.closest(observedSelector);
    })) schedule();
  }).observe(document.body, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'style', 'value'] });
  document.addEventListener('change', schedule);
  document.addEventListener('variant:changed', schedule);
  document.addEventListener('cart:updated', schedule);
  schedule();
})();
